import api from './api';

/**
 * Get paginated list of packages with filters
 * @param {Object} filters - Filter parameters
 * @param {number} page - Page number (default: 1)
 * @param {number} per_page - Items per page (default: 20)
 * @returns {Promise<Object>} Response with packages data and pagination info
 */
export const getPackages = async (filters = {}, page = 1, per_page = 1000) => {
  console.log('🚀 packagesService.getPackages called with:', { filters, page, per_page });
  
  try {
    // Build query parameters
    const params = new URLSearchParams({
      page: page.toString(),
      per_page: per_page.toString()
    });

    // Add filters to params
    if (filters.search) params.append('search', filters.search);
    if (filters.status) params.append('status', filters.status);
    if (filters.destination_id) params.append('destination_id', filters.destination_id);
    if (filters.sub_destination_id) params.append('sub_destination_id', filters.sub_destination_id);
    if (filters.nights) params.append('nights', filters.nights.toString());
    if (filters.days) params.append('days', filters.days.toString());
    if (filters.is_featured !== undefined) params.append('is_featured', filters.is_featured.toString());
    if (filters.sort_by) params.append('sort_by', filters.sort_by);
    if (filters.sort_order) params.append('sort_order', filters.sort_order);

    const url = `/api/admin/packages?${params.toString()}`;
    console.log('📡 API URL:', url);

    const response = await api.get(url);
    console.log('✅ API Response received:', {
      status: response.status,
      dataType: typeof response.data,
      isArray: Array.isArray(response.data),
      dataLength: Array.isArray(response.data) ? response.data.length : 'N/A',
      meta: response.meta || 'No meta'
    });

    // Handle different response formats
    if (response.meta && response.meta.pagination) {
      // Paginated response format
      console.log('📄 Paginated response detected');
      return {
        data: response.data || [],
        pagination: response.meta.pagination,
        success: true
      };
    } else if (response.data && Array.isArray(response.data)) {
      // Direct array response
      console.log('📋 Array response detected');
      return {
        data: response.data,
        pagination: {
          page: 1,
          per_page: response.data.length,
          total: response.data.length,
          pages: 1
        },
        success: true
      };
    } else {
      console.log('⚠️ Unexpected response format:', response);
      return {
        data: [],
        pagination: { page: 1, per_page: 0, total: 0, pages: 0 },
        success: false
      };
    }

  } catch (error) {
    console.error('❌ packagesService.getPackages error:', {
      message: error.message,
      status: error.response?.status,
      statusText: error.response?.statusText,
      data: error.response?.data,
      config: {
        url: error.config?.url,
        method: error.config?.method,
        headers: error.config?.headers
      }
    });

    // Enhanced error handling for specific status codes
    if (error.response?.status === 403) {
      throw new Error(`403 Forbidden: Access denied to packages API. Please check your permissions or contact administrator.`);
    } else if (error.response?.status === 401) {
      throw new Error(`401 Unauthorized: Session expired. Please log in again.`);
    } else if (error.response?.status === 404) {
      throw new Error(`404 Not Found: Packages API endpoint not found.`);
    } else if (error.response?.status >= 500) {
      throw new Error(`Server Error (${error.response.status}): The server is experiencing issues. Please try again later.`);
    } else {
      throw error;
    }
  }
};

/**
 * Get single package by ID
 * @param {string} id - Package ID
 * @returns {Promise<Object>} Package data
 */
export const getPackageById = async (id) => {
  console.log('🔍 packagesService.getPackageById called with ID:', id);
  
  try {
    const response = await api.get(`/api/admin/packages/${id}`);
    console.log('✅ Package details received:', response.data);
    return response.data;
  } catch (error) {
    console.error('❌ packagesService.getPackageById error:', error);
    throw error;
  }
};

/**
 * Create new package
 * @param {Object} packageData - Package data
 * @returns {Promise<Object>} Created package
 */
export const createPackage = async (packageData) => {
  console.log('➕ packagesService.createPackage called with:', packageData);
  
  try {
    const response = await api.post('/api/admin/packages', packageData);
    console.log('✅ Package created:', response.data);
    return response.data;
  } catch (error) {
    console.error('❌ packagesService.createPackage error:', error);
    console.error('❌ Error details:', {
      message: error.message,
      response: error.response?.data,
      status: error.response?.status,
      headers: error.response?.headers
    });
    throw error;
  }
};

/**
 * Update existing package
 * @param {string} id - Package ID
 * @param {Object} packageData - Updated package data
 * @returns {Promise<Object>} Updated package
 */
export const updatePackage = async (id, packageData) => {
  console.log('✏️ packagesService.updatePackage called with:', { id, packageData });
  
  try {
    const response = await api.put(`/api/admin/packages/${id}`, packageData);
    console.log('✅ Package updated:', response.data);
    return response.data;
  } catch (error) {
    console.error('❌ packagesService.updatePackage error:', error);
    throw error;
  }
};

/**
 * Delete package
 * @param {string} id - Package ID
 * @returns {Promise<Object>} Deletion confirmation
 */
export const deletePackage = async (id) => {
  console.log('🗑️ packagesService.deletePackage called with ID:', id);
  
  try {
    const response = await api.delete(`/api/admin/packages/${id}`);
    console.log('✅ Package deleted:', response.data);
    return response.data;
  } catch (error) {
    console.error('❌ packagesService.deletePackage error:', error);
    throw error;
  }
};

/**
 * Create itinerary day for a package
 * @param {string} packageId - Package ID
 * @param {Object} dayData - Itinerary day data
 * @returns {Promise<Object>} Created itinerary day
 */
export const createItineraryDay = async (packageId, dayData) => {
  console.log('📋 packagesService.createItineraryDay called with:', { packageId, dayData });
  
  try {
    const response = await api.post(`/api/admin/packages/${packageId}/itinerary`, dayData);
    console.log('✅ Itinerary day created:', response.data);
    return response.data;
  } catch (error) {
    console.error('❌ packagesService.createItineraryDay error:', error);
    throw error;
  }
};

/**
 * Update itinerary day for a package
 * @param {string} packageId - Package ID
 * @param {number} dayNumber - Day number
 * @param {Object} dayData - Itinerary day data
 * @returns {Promise<Object>} Updated itinerary day
 */
export const updateItineraryDay = async (packageId, dayNumber, dayData) => {
  console.log('📋 packagesService.updateItineraryDay called with:', { packageId, dayNumber, dayData });
  
  try {
    const response = await api.put(`/api/admin/packages/${packageId}/itinerary/${dayNumber}`, dayData);
    console.log('✅ Itinerary day updated:', response.data);
    return response.data;
  } catch (error) {
    console.error('❌ packagesService.updateItineraryDay error:', error);
    throw error;
  }
};

/**
 * Delete itinerary day for a package
 * @param {string} packageId - Package ID
 * @param {number} dayNumber - Day number
 * @returns {Promise<Object>} Delete result
 */
export const deleteItineraryDay = async (packageId, dayNumber) => {
  console.log('📋 packagesService.deleteItineraryDay called with:', { packageId, dayNumber });
  
  try {
    const response = await api.delete(`/api/admin/packages/${packageId}/itinerary/${dayNumber}`);
    console.log('✅ Itinerary day deleted:', response.data);
    return response.data;
  } catch (error) {
    console.error('❌ packagesService.deleteItineraryDay error:', error);
    throw error;
  }
};

/**
 * Get package itinerary
 * @param {string} packageId - Package ID
 * @returns {Promise<Array>} Package itinerary days
 */
export const getPackageItinerary = async (packageId) => {
  console.log('📋 packagesService.getPackageItinerary called with:', { packageId });
  
  try {
    const response = await api.get(`/api/admin/packages/${packageId}/itinerary`);
    console.log('✅ Package itinerary retrieved:', response.data);
    return response.data;
  } catch (error) {
    console.error('❌ packagesService.getPackageItinerary error:', error);
    throw error;
  }
};

/**
 * Bulk update package itinerary
 * @param {string} packageId - Package ID
 * @param {Array} itineraryDays - Array of itinerary days
 * @returns {Promise<Object>} Update result
 */
export const bulkUpdateItinerary = async (packageId, itineraryDays) => {
  console.log('📋 packagesService.bulkUpdateItinerary called with:', { packageId, itineraryDays });
  
  try {
    const response = await api.put(`/api/admin/packages/${packageId}/itinerary`, {
      days: itineraryDays
    });
    console.log('✅ Package itinerary bulk updated:', response.data);
    return response.data;
  } catch (error) {
    console.error('❌ packagesService.bulkUpdateItinerary error:', error);
    throw error;
  }
};